// Recent conversational text from the salon-scoped transcript endpoint.
// Checkpoint history can be compacted and is not a record of client delivery.
// Internal summaries, planning turns and non-text blocks are excluded server-side.

import { useEffect, useState, useCallback } from 'preact/hooks';
import { apiFetch, ApiError } from '../api/client';
import { ASSISTANT_INITIAL, ASSISTANT_NAME } from '../brand.js';

function firstNameOf(name) {
  if (!name) return 'this guest';
  return String(name).trim().split(/\s+/)[0] || 'this guest';
}

export function TranscriptReplayModal({ conversationId, guestName, onClose }) {
  const [state, setState] = useState('loading'); // loading | ready | empty | error
  const [messages, setMessages] = useState([]);

  const load = useCallback(async () => {
    if (!conversationId) return;
    setState('loading');
    try {
      const data = await apiFetch(
        `/v1/owner/conversations/${encodeURIComponent(conversationId)}/transcript`,
      );
      const msgs = (data && Array.isArray(data.messages) && data.messages) || [];
      if (!msgs.length) {
        setMessages([]);
        setState('empty');
        return;
      }
      setMessages(msgs);
      setState('ready');
    } catch (err) {
      // 404 = endpoint not deployed yet OR no checkpoint for this thread; both
      // land in a calm empty panel rather than crashing the modal.
      if (err instanceof ApiError && err.status === 404) {
        setState('empty');
      } else {
        setState('error');
      }
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    load();
  }, [load]);

  // Escape closes the modal (matches the .dc.html backdrop click-to-close).
  useEffect(() => {
    function handleEscape(e) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const first = firstNameOf(guestName);

  return (
    <div
      onClick={() => onClose?.()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(34,30,23,.55)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Recent conversation"
        aria-modal="true"
        className="replay-stage"
        style={{ display: 'flex', gap: '28px', alignItems: 'center' }}
      >
        {/* left rail — the framing copy from the .dc.html overlay */}
        <div className="replay-rail" style={{ maxWidth: '230px', color: '#F3EFE6' }}>
          <div
            className="font-serif"
            style={{
              fontStyle: 'italic',
              fontSize: '24px',
              marginBottom: '12px',
              lineHeight: 1.2,
            }}
          >
            Recent conversation
          </div>
          <div
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#D8D2C4',
              lineHeight: 1.55,
              marginBottom: '20px',
            }}
          >
            Recent saved messages with {first}. Earlier messages may be missing.
            This view does not confirm which messages reached their device.
          </div>
          <div
            onClick={() => onClose?.()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              fontWeight: 700,
              color: '#221E17',
              background: '#F3EFE6',
              padding: '9px 16px',
              borderRadius: '100px',
              cursor: 'pointer',
            }}
          >
            ← Back to {first}'s card
          </div>
        </div>

        {/* phone */}
        <div
          style={{
            width: 'min(368px, calc(100vw - 40px))',
            height: '740px',
            maxHeight: '90vh',
            background: '#1C1A16',
            borderRadius: '44px',
            padding: '11px',
            boxShadow: '0 40px 90px rgba(0,0,0,.45)',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              background: '#EFEAE0',
              borderRadius: '34px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            {/* phone header */}
            <div
              style={{
                flex: 'none',
                background: '#FBF8F1',
                padding: '16px 20px 14px',
                borderBottom: '1px solid rgba(34,30,23,.07)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div
                className="font-serif"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(140deg,#56683F,#33401E)',
                  color: '#F3EFE6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontStyle: 'italic',
                  fontWeight: 600,
                  fontSize: '18px',
                }}
              >
                {ASSISTANT_INITIAL}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#221E17' }}>
                  {ASSISTANT_NAME}
                </div>
                <div
                  style={{
                    fontSize: '11.5px',
                    fontWeight: 600,
                    color: '#56683F',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#56683F',
                      display: 'inline-block',
                    }}
                  />
                  the front desk
                </div>
              </div>
              <span
                onClick={() => onClose?.()}
                style={{
                  fontSize: '16px',
                  color: '#A89E8E',
                  cursor: 'pointer',
                  padding: '4px 8px',
                }}
              >
                ✕
              </span>
            </div>

            {/* transcript */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '18px 16px 8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              {state === 'loading' && (
                <div
                  style={{
                    margin: 'auto',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#9A9082',
                  }}
                >
                  Loading the conversation…
                </div>
              )}

              {state === 'empty' && (
                <div
                  style={{
                    margin: 'auto',
                    textAlign: 'center',
                    padding: '0 24px',
                    color: '#7A7060',
                  }}
                >
                  <div
                    className="font-serif"
                    style={{ fontStyle: 'italic', fontSize: '18px', marginBottom: '6px' }}
                  >
                    No saved messages available
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 500, lineHeight: 1.45 }}>
                    There are no recent conversation messages to display.
                  </div>
                </div>
              )}

              {state === 'error' && (
                <div
                  style={{
                    margin: 'auto',
                    textAlign: 'center',
                    padding: '0 24px',
                    color: '#7A7060',
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>
                    Couldn't load the conversation.
                  </div>
                  <div
                    onClick={() => load()}
                    style={{
                      display: 'inline-block',
                      fontSize: '13px',
                      fontWeight: 700,
                      color: '#56683F',
                      background: '#EEF0E5',
                      border: '1px solid rgba(86,104,63,.22)',
                      padding: '8px 16px',
                      borderRadius: '100px',
                      cursor: 'pointer',
                    }}
                  >
                    Try again
                  </div>
                </div>
              )}

              {state === 'ready' &&
                messages.map((m, i) => (
                  <Bubble key={i} message={m} />
                ))}
            </div>

            <div style={{ padding: '12px 16px', background: '#FBF8F1', color: '#7A7060', fontSize: '12px' }}>
              Recent saved messages · Read only · May be incomplete
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .replay-stage {
          animation: replayPop 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
        }
        @keyframes replayPop {
          from { transform: scale(0.94); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @media (max-width: 760px) {
          .replay-rail { display: none; }
        }
      `}</style>
    </div>
  );
}

// One transcript row. The stored shape carries role ('guest' | 'jonathan'),
// plain text, and an optional 'photo' attachment marker. Guest turns sit on the
// right in lavender; Jonathan sits on the left in cream — matching the .dc.html
// bubble anatomy (radii, palette). A photo turn renders the generic selfie tile
// (no real bytes are ever sent down this channel).
function Bubble({ message }) {
  const isGuest = message.role === 'guest';

  if (message.attachment === 'photo') {
    return (
      <div style={{ display: 'flex', justifyContent: isGuest ? 'flex-end' : 'flex-start' }}>
        <div style={{ maxWidth: '80%' }}>
          <div
            style={{
              width: '150px',
              height: '188px',
              borderRadius: '18px 18px 5px 18px',
              background: 'linear-gradient(160deg,#cdb49b,#9c8468 60%,#7d6951)',
              display: 'flex',
              alignItems: 'flex-end',
              padding: '12px',
              boxShadow: '0 6px 16px rgba(34,30,23,.18)',
            }}
          >
            <span
              style={{
                fontSize: '11.5px',
                fontWeight: 700,
                color: 'rgba(255,255,255,.92)',
                background: 'rgba(34,30,23,.32)',
                padding: '4px 9px',
                borderRadius: '100px',
                backdropFilter: 'blur(4px)',
              }}
            >
              📷 selfie
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: isGuest ? 'flex-end' : 'flex-start' }}>
      <div
        style={{
          maxWidth: '80%',
          padding: '11px 15px',
          borderRadius: isGuest ? '18px 18px 5px 18px' : '18px 18px 18px 5px',
          background: isGuest ? '#8D9FF1' : '#FBF8F1',
          color: isGuest ? '#1c2247' : '#221E17',
          border: isGuest ? 'none' : '1px solid rgba(34,30,23,.07)',
          fontSize: '14.5px',
          lineHeight: 1.45,
          fontWeight: 500,
          whiteSpace: 'pre-wrap',
        }}
      >
        {message.text}
      </div>
    </div>
  );
}
